// src/components/StudentRegistration.jsx
// Public student registration form

import React, { useState, useEffect } from 'react';
import { registerStudent } from '../firebase/registration';
import { getCohortByCode } from '../firebase/cohorts';
import { DEFAULT_ORG_ID } from '../firebase/orgs';
import { getOrgBranding } from '../firebase/orgBranding';
import { setPendingTutorAction } from '../firebase/tutorLinks';
import useIsMobile from '../hooks/useIsMobile';

function StudentRegistration({ onRegistrationSuccess, onVerificationPending, cohortCodeFromUrl = '', orgIdFromUrl = '' }) {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cohortCode: cohortCodeFromUrl || '',
    password: '',
    confirmPassword: ''
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Linked tutors: an existing account can't register again, but can log in
  // and have this tutor added to it instead
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Only resolvable when arriving via a cohort-coded registration link - a
  // student who lands on /register with no code gets the generic logo
  // below, same reasoning as Login.jsx (no other way to know whose form
  // this is before anything's submitted).
  const [isAileenOrg, setIsAileenOrg] = useState(false);
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);

  useEffect(() => {
    if (!cohortCodeFromUrl) return;
    getCohortByCode(cohortCodeFromUrl, orgIdFromUrl).then(cohort => {
      const orgId = cohort?.orgId || DEFAULT_ORG_ID;
      const aileen = orgId === DEFAULT_ORG_ID;
      setIsAileenOrg(aileen);
      if (!aileen) {
        getOrgBranding(orgId).then(branding => setOrgLogoUrl(branding?.logoUrl || null));
      }
    }).catch(err => console.error('Cohort branding lookup error:', err));
  }, [cohortCodeFromUrl, orgIdFromUrl]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.email || !formData.phone || !formData.cohortCode || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      setError('Please confirm you agree to the Terms and Conditions and have read the Privacy Policy.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const userData = await registerStudent({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        cohortCode: formData.cohortCode.trim().toUpperCase(),
        orgId: orgIdFromUrl,
        password: formData.password,
        agreedToTerms
      });
      
      // Registration successful
      if (userData.requiresVerification) {
        setVerificationSent(true);
      } else {
        // No email verification — user is still signed in, safe to read Firestore settings
        try {
          const { sendDirectNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
          sendDirectNotification(NOTIFICATION_TYPES.NEW_REGISTRATION, {
            studentName: formData.name,
            studentEmail: formData.email,
            studentPhone: formData.phone,
            cohortCode: formData.cohortCode.trim().toUpperCase()
          });
        } catch (e) { console.error('Notification error:', e); }
        onRegistrationSuccess(userData);
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
      setAlreadyRegistered(/already registered/i.test(err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f1e8 0%, #dbd9db 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '12px' : '20px',
        fontFamily: "'Crimson Pro', 'Georgia', serif"
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '28px 20px' : '48px 40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center'
        }}>
          {isAileenOrg ? (
            <img
              src="/aileen-edgar-logo.png"
              alt="Aileen Edgar ACCA ATX Tutor"
              style={{ maxWidth: '240px', width: '100%', height: 'auto', marginBottom: '24px' }}
            />
          ) : orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt="Logo"
              style={{ maxWidth: '240px', width: '100%', height: 'auto', marginBottom: '24px' }}
            />
          ) : (
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#000', marginBottom: '24px' }}>
              Homework Solutions
            </div>
          )}
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: '#d4edda', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 20px auto', fontSize: '32px'
          }}>
            ✉️
          </div>
          <h1 style={{ margin: '0 0 12px 0', fontSize: '26px', color: '#000', fontWeight: '700' }}>
            Check Your Email
          </h1>
          <p style={{ margin: '0 0 8px 0', color: '#4a5568', fontSize: '15px', lineHeight: '1.6' }}>
            We've sent a verification link to <strong>{formData.email}</strong>.
          </p>
          <p style={{ margin: '0 0 32px 0', color: '#718096', fontSize: '14px', lineHeight: '1.6' }}>
            Please click the link in the email to verify your account, then come back and log in.
          </p>
          <button
            onClick={onVerificationPending}
            style={{
              padding: '14px 32px',
              background: '#c33329',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f1e8 0%, #dbd9db 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '12px' : '20px',
      fontFamily: "'Crimson Pro', 'Georgia', serif"
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: isMobile ? '28px 20px' : '40px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          {isAileenOrg ? (
            <img
              src="/aileen-edgar-logo.png"
              alt="Aileen Edgar ACCA ATX Tutor"
              style={{ maxWidth: '280px', width: '100%', height: 'auto', marginBottom: '16px' }}
            />
          ) : orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt="Logo"
              style={{ maxWidth: '280px', width: '100%', height: 'auto', marginBottom: '16px' }}
            />
          ) : (
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#000', marginBottom: '16px' }}>
              Homework Solutions
            </div>
          )}
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', color: '#000000', fontWeight: '700' }}>
            Student Registration
          </h1>
          <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
            Create your homework portal account
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#4a5568',
              fontSize: '14px'
            }}>
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              placeholder="Enter your full name"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c33329'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#4a5568',
              fontSize: '14px'
            }}>
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              placeholder="your.email@example.com"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c33329'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Phone */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#4a5568',
              fontSize: '14px'
            }}>
              Phone Number (for WhatsApp) *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
              placeholder="+44 7700 900000"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c33329'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Cohort Code */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#4a5568',
              fontSize: '14px'
            }}>
              Cohort Code *
            </label>
            <input
              type="text"
              name="cohortCode"
              value={formData.cohortCode}
              onChange={cohortCodeFromUrl ? undefined : handleChange}
              readOnly={!!cohortCodeFromUrl}
              disabled={loading}
              placeholder="Enter the code from your teacher"
              style={{
                width: '100%',
                padding: '12px',
                border: cohortCodeFromUrl ? '2px solid #1b998b' : '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                textTransform: 'uppercase',
                background: cohortCodeFromUrl ? '#f0fdf4' : 'white'
              }}
              onFocus={cohortCodeFromUrl ? undefined : (e) => e.target.style.borderColor = '#c33329'}
              onBlur={cohortCodeFromUrl ? undefined : (e) => e.target.style.borderColor = '#e2e8f0'}
            />
            <div style={{
              marginTop: '6px',
              fontSize: '12px',
              color: cohortCodeFromUrl ? '#1b998b' : '#718096',
              fontWeight: cohortCodeFromUrl ? '600' : 'normal'
            }}>
              {cohortCodeFromUrl ? 'Pre-filled from your registration link' : 'Your teacher will provide this code'}
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#4a5568',
              fontSize: '14px'
            }}>
              Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              placeholder="At least 6 characters"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c33329'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#4a5568',
              fontSize: '14px'
            }}>
              Confirm Password *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              placeholder="Re-enter your password"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c33329'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Terms and Privacy Policy agreement */}
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#4a5568',
            lineHeight: 1.5,
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => { setAgreedToTerms(e.target.checked); setError(''); }}
              disabled={loading}
              style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0, accentColor: '#c33329' }}
            />
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#c33329', fontWeight: 600 }}>Terms and Conditions</a>
              {' '}and have read the{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#c33329', fontWeight: 600 }}>Privacy Policy</a>.
            </span>
          </label>

          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '14px'
            }}>
              {error}
              {alreadyRegistered && !orgIdFromUrl && (
                <div style={{ marginTop: '10px', color: '#7f1d1d' }}>
                  Already studying with another tutor on Homework Solutions? Log in with your existing account and use "Add another tutor" on your dashboard.
                </div>
              )}
              {alreadyRegistered && orgIdFromUrl && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ marginBottom: '8px', color: '#7f1d1d' }}>
                    Already studying with another tutor on Homework Solutions? Log in with your existing account and this tutor will be added to it.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPendingTutorAction({ type: 'join', orgId: orgIdFromUrl, cohortCode: formData.cohortCode.trim().toUpperCase() });
                      window.location.href = '/';
                    }}
                    style={{ padding: '8px 14px', background: '#991b1b', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                  >
                    Log in to add this tutor
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#a0aec0' : 'linear-gradient(135deg, #c33329 0%, #c33329 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.transform = 'scale(1.02)')}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          {/* Login Link */}
          <div style={{
            marginTop: '24px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#718096'
          }}>
            Already have an account?{' '}
            <a
              href="/login"
              style={{
                color: '#c33329',
                textDecoration: 'none',
                fontWeight: '600'
              }}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/';
              }}
            >
              Log in here
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StudentRegistration;